<template>
  <div>
    <v-container fluid>
      <!-- Header Toolbar -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ $t('_contactAgentChatLogs') || 'Contact Agent Chat Log' }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- Main Content Card -->
      <v-card class="card2" style="margin-top: 1rem">
        <v-card-title class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3" size="28">mdi-headset</v-icon>
            <h3 class="text-h5 font-weight-bold text-primary">Contact Agent Chat Log</h3>
          </div>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Filter Section - Redesigned -->
          <v-card variant="outlined" class="filter-card mb-4">
            <v-card-text class="pa-4">
              <v-row align="center" class="filter-row">
                <!-- Date Range -->
                <v-col cols="12" sm="6" md="3">
                  <v-menu v-model="startDateFlag" :close-on-content-click="false" transition="scale-transition" location="bottom" min-width="auto">
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="startDate"
                        label="Start Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        variant="outlined"
                        v-bind="props"
                        density="compact"
                        color="primary"
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      @update:model-value="(val) => { startDate = formatDate(val); startDateFlag = false; }"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-menu v-model="endDateFlag" :close-on-content-click="false" transition="scale-transition" location="bottom" min-width="auto">
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="endDate"
                        label="End Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        variant="outlined"
                        v-bind="props"
                        density="compact"
                        color="primary"
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      @update:model-value="(val) => { endDate = formatDate(val); endDateFlag = false; }"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <!-- User Channel -->
                <v-col cols="12" sm="6" md="3">
                  <v-text-field 
                    label="User Channel" 
                    v-model="userChannel" 
                    clearable 
                    variant="outlined" 
                    density="compact" 
                    prepend-inner-icon="mdi-account-search" 
                    color="primary"
                    hide-details
                    placeholder="Search by name or ID"
                  ></v-text-field>
                </v-col>
                <!-- Search Button -->
                <v-col cols="12" sm="6" md="3" class="d-flex align-end">
                  <v-spacer></v-spacer>
                  <v-btn 
                    color="primary" 
                    variant="elevated" 
                    size="large" 
                    @click="Search" 
                    class="search-btn" 
                    prepend-icon="mdi-magnify"
                    :loading="tableLoading"
                    block
                  >
                    {{ $t('search') || 'Search' }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Statistics Bar -->
          <div class="stats-bar mb-4">
            <v-chip color="primary" variant="tonal" size="large" class="stats-chip">
              <v-icon start>mdi-account-group</v-icon>
              {{ searchTotalTable }} Conversations
            </v-chip>
          </div>

          <!-- Data Table -->
          <v-card class="data-table-card">
            <div class="table-container">
              <v-data-table-server
                v-model:options="options"
                :items-per-page="options.itemsPerPage"
                :items-length="searchTotalTable"
                :items="dataTable"
                :headers="headers"
                :items-per-page-options="[10, 25, 50, 100]"
                :loading="tableLoading"
                class="modern-table"
                density="comfortable"
                hover
              >
                <template #top>
                  <v-toolbar flat color="grey-lighten-4" class="table-toolbar">
                    <v-toolbar-title class="text-h6 font-weight-medium">
                      <v-icon class="mr-2" color="primary">mdi-table</v-icon>
                      Conversation Data ({{ searchTotalTable }} records)
                    </v-toolbar-title>
                  </v-toolbar>
                </template>

                <template #item="{ item }">
                  <tr class="table-row">
                    <!-- No. -->
                    <td class="text-center">
                      {{ item.number }}
                    </td>
                    <!-- Name / UserChannel (ID) -->
                    <td>
                      <div class="d-flex align-center">
                        <v-avatar size="36" color="primary" class="mr-2">
                          <v-icon color="white" size="20">mdi-account</v-icon>
                        </v-avatar>
                        <div>
                          <div class="font-weight-medium">{{ item.displayName || '-' }}</div>
                          <div class="text-caption text-grey">{{ item.userChannel }}</div>
                        </div>
                      </div>
                    </td>
                    <!-- Recent Message -->
                    <td>
                      <div class="text-container">
                        <span class="text-content">{{ truncateText(item.recentMessage, 50) }}</span>
                      </div>
                    </td>
                    <!-- DateTime -->
                    <td class="text-center">
                      <div class="d-flex flex-column align-center">
                        <span class="text-body-2">{{ formatDateTime(item.lastMessageTime) }}</span>
                      </div>
                    </td>
                    <!-- Action -->
                    <td class="text-center">
                      <v-btn 
                        size="small" 
                        variant="tonal" 
                        color="primary" 
                        @click="openChatDialog(item)" 
                        class="chat-btn"
                      >
                        <v-icon size="18" class="mr-1">mdi-forum</v-icon>
                        View Chat
                        <v-tooltip activator="parent" location="top">View Conversation</v-tooltip>
                      </v-btn>
                    </td>
                  </tr>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center pa-8">
                    <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-forum-outline</v-icon>
                    <h3 class="text-h6 text-grey mb-2">No conversations found</h3>
                    <p class="text-body-2 text-grey">Try adjusting your filters or date range</p>
                  </div>
                </template>
              </v-data-table-server>
            </div>
          </v-card>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Chat Conversation Dialog - Modern Design -->
    <v-dialog v-model="chatDialog" max-width="1000" scrollable transition="dialog-bottom-transition" :scrim="true">
      <v-card class="chat-dialog-card">
        <!-- Dialog Header - Modern Style -->
        <v-card-title class="chat-dialog-header pa-0">
          <div class="chat-header-content">
            <div class="d-flex align-center">
              <div class="chat-icon-wrapper">
                <v-icon class="chat-icon" size="24">mdi-forum</v-icon>
              </div>
              <div class="chat-header-text">
                <div class="chat-title">{{ selectedConversation?.displayName || 'User' }}</div>
                <div class="chat-subtitle">{{ selectedConversation?.userChannel || '-' }}</div>
              </div>
            </div>
            <v-spacer></v-spacer>
            <div class="header-stats d-flex align-center ga-2 mr-4">
              <v-chip color="white" variant="outlined" size="small" class="header-chip">
                <v-icon start size="14">mdi-message-text</v-icon>
                {{ getTotalMessages }} messages
              </v-chip>
              <v-chip color="white" variant="outlined" size="small" class="header-chip">
                <v-icon start size="14">mdi-history</v-icon>
                {{ chatSessions?.length || 0 }} sessions
              </v-chip>
            </div>
            <v-btn icon variant="text" class="close-btn" @click="chatDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <!-- Dialog Content - Chat Messages -->
        <v-card-text ref="chatContent" class="chat-dialog-content pa-0">
          <div v-if="chatSessions && chatSessions.length > 0" class="chat-messages-wrapper">
            <div class="chat-messages-container">
              <div v-for="(session, sIndex) in chatSessions" :key="sIndex" class="chat-session">
                <!-- Session Divider - Start -->
                <div class="session-divider">
                  <div class="session-divider-line"></div>
                  <v-chip color="success" variant="flat" size="small" class="session-chip">
                    <v-icon start size="14">mdi-play-circle</v-icon>
                    Session Start: {{ session.sessionId }}
                  </v-chip>
                  <div class="session-divider-line"></div>
                </div>

                <!-- Session Time Info -->
                <div class="session-time-info">
                  <span class="session-date">{{ formatSessionDate(session.startTime) }}</span>
                </div>

                <!-- Messages in Session -->
                <div v-for="(msg, mIndex) in session.messages" :key="mIndex" class="message-wrapper" :class="msg.isAdmin ? 'admin-wrapper' : 'user-wrapper'">
                  <!-- User Message (Left) -->
                  <div v-if="!msg.isAdmin" class="message-row user-row">
                  <div class="message-avatar user-avatar">
                    <v-icon color="white" size="18">mdi-account</v-icon>
                  </div>
                  <div class="message-bubble user-bubble">
                    <div class="message-sender">User</div>
                    <div class="message-content">{{ msg.text }}</div>
                    <!-- Attachment Preview -->
                    <div v-if="msg.attachment" class="message-attachment">
                      <div v-if="msg.attachment.type === 'image'" class="attachment-image" @click="openImagePreview(msg.attachment.url)">
                        <v-img :src="msg.attachment.url" max-width="200" max-height="150" cover class="rounded-lg cursor-pointer" />
                      </div>
                      <div v-else class="attachment-file">
                        <v-icon color="primary" class="mr-2">mdi-file-document</v-icon>
                        <span class="file-name">{{ msg.attachment.name }}</span>
                      </div>
                    </div>
                    <div class="message-time">{{ formatMessageTime(msg.timestamp) }}</div>
                  </div>
                </div>

                <!-- Admin Message (Right) -->
                <div v-else class="message-row admin-row">
                  <div class="message-bubble admin-bubble">
                    <div class="message-sender">{{ msg.adminName }}</div>
                    <div class="message-content">{{ msg.text }}</div>
                    <!-- Attachment Preview -->
                    <div v-if="msg.attachment" class="message-attachment">
                      <div v-if="msg.attachment.type === 'image'" class="attachment-image" @click="openImagePreview(msg.attachment.url)">
                        <v-img :src="msg.attachment.url" max-width="200" max-height="150" cover class="rounded-lg cursor-pointer" />
                      </div>
                      <div v-else class="attachment-file">
                        <v-icon color="white" class="mr-2">mdi-file-document</v-icon>
                        <span class="file-name">{{ msg.attachment.name }}</span>
                      </div>
                    </div>
                    <div class="message-time">{{ formatMessageTime(msg.timestamp) }}</div>
                  </div>
                  <div class="message-avatar admin-avatar">
                    <v-icon color="white" size="18">mdi-headset</v-icon>
                  </div>
                </div>
              </div>

              <!-- Session Divider - End -->
              <div class="session-divider session-end">
                <div class="session-divider-line session-line-end-color"></div>
                <v-chip color="error" variant="flat" size="small" class="session-chip">
                  <v-icon start size="14">mdi-stop-circle</v-icon>
                  Session End: {{ session.sessionId }}
                </v-chip>
                <div class="session-divider-line session-line-end-color"></div>
              </div>
            </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="chat-empty-state">
            <div class="empty-icon-wrapper">
              <v-icon size="64" color="grey-lighten-1">mdi-message-off-outline</v-icon>
            </div>
            <h3 class="text-h6 text-grey mt-4">No messages found</h3>
            <p class="text-body-2 text-grey-darken-1 mt-2">This conversation has no messages to display</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Image Preview Dialog -->
    <v-dialog v-model="imagePreviewDialog" max-width="900">
      <v-card class="image-preview-card">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Image Preview</span>
          <v-btn icon variant="text" @click="imagePreviewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-img :src="previewImageUrl" max-height="70vh" contain />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay v-model="isLoading" class="align-center justify-center" persistent>
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ContactAgentChatLog',
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  data: () => ({
    // Date Range
    startDate: null,
    endDate: null,
    startDateFlag: false,
    endDateFlag: false,

    // Filters
    userChannel: null,

    // Table
    dataTable: [],
    tableLoading: false,
    searchTotalTable: 0,
    isInitialized: false,
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    },

    // Chat Dialog
    chatDialog: false,
    selectedConversation: null,
    chatSessions: [],

    // Image Preview
    imagePreviewDialog: false,
    previewImageUrl: '',

    // UI State
    isLoading: false,
    today: null,
  }),
  computed: {
    headers() {
      return [
        { title: 'No.', align: 'center', sortable: false, width: '80px', key: 'number' },
        { title: 'User', align: 'start', sortable: false, width: '250px', key: 'displayName' },
        { title: 'Recent Message', align: 'start', sortable: false, key: 'recentMessage' },
        { title: 'DateTime', align: 'center', sortable: false, width: '220px', key: 'lastMessageTime' },
        { title: 'Action', align: 'center', sortable: false, width: '240px', key: 'action' },
      ];
    },
    getTotalMessages() {
      if (!this.chatSessions) return 0;
      return this.chatSessions.reduce((total, session) => total + session.messages.length, 0);
    },
  },
  watch: {
    options: {
      handler() {
        if (this.isInitialized) {
          this.Get();
        }
      },
      deep: true,
    },
  },
  methods: {
    // Date Formatting
    formatDate(dateInput) {
      if (!dateInput) return null;
      return moment(dateInput).format('YYYY-MM-DD');
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      return moment(dateTime).format('DD/MM/YYYY HH:mm');
    },
    formatMessageTime(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('HH:mm');
    },
    formatSessionDate(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('DD MMM YYYY, HH:mm');
    },
    convertDateToString(date) {
      return moment(date).format('YYYY-MM-DD');
    },

    // Utility Functions
    truncateText(text, maxLength) {
      if (!text) return '-';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    clearFilters() {
      this.userChannel = null;
    },

    // Get Date
    GetDate() {
      const now = new Date();
      this.today = moment(now).format('DD/MM/YYYY');
      this.startDate = this.convertDateToString(now);
      this.endDate = this.convertDateToString(now);
    },

    // Search Function
    Search() {
      this.options.page = 1;
      this.Get();
    },

    // Get Data from API
    Get() {
      const self = this;
      self.tableLoading = true;
      
      const { page, itemsPerPage } = this.options;
      
      const request = {
        page: page,
        perPage: itemsPerPage !== -1 ? itemsPerPage : self.searchTotalTable || 1000,
        startDate: self.startDate,
        endDate: self.endDate,
        type: null,
        UserChannel: self.userChannel || null,
        SessionID: null,
      };
      
      self.$call.GetTableContactAdminChatLog(
        request,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            self.searchTotalTable = dataResponse.data.total;
            console.log('Contact Admin Chat Log Data:', dataResponse.data);
            self.dataTable = dataResponse.data.data.map((item) => ({
              number: item.number,
              displayName: item.userDisplayName,
              userChannel: item.userChannel,
              recentMessage: item.latestMessage,
              lastMessageTime: item.createDate,
            }));
            self.options.page = dataResponse.data.page;
          } else {
            self.dataTable = [];
            self.searchTotalTable = 0;
          }
          self.tableLoading = false;
        },
        (errorResponse) => {
          self.tableLoading = false;
          console.error('Error fetching contact admin chat log:', errorResponse);
          self.dataTable = [];
          self.searchTotalTable = 0;
        }
      );
    },

    // Open Chat Dialog
    openChatDialog(item) {
      this.selectedConversation = item;
      this.loadConversation(item);
      this.chatDialog = true;
    },

    // Load Conversation from API
    loadConversation(item) {
      
      const self = this;
      self.isLoading = true;
      self.chatSessions = [];
      
      const request = {
        USER_CHANNEL: item.userChannel,
        Take: 100, // Get more messages for history
        ID: null,
        TopicID: null,
      };
      
      self.$call.GetHistoryMessage(
        request,
        {},
        (dataResponse) => {
          if (dataResponse.status === 0 && dataResponse.data?.data) {
            // Group messages by SessionID
            const messages = dataResponse.data.data;
            const sessionMap = {};
            
            messages.forEach((msg) => {
              const sessionId = msg.sessionID || 'default';
              if (!sessionMap[sessionId]) {
                sessionMap[sessionId] = {
                  sessionId: sessionId,
                  startTime: null,
                  endTime: null,
                  messages: [],
                };
              }
              
              // Add user message
              if (msg.messageUser) {
                sessionMap[sessionId].messages.push({
                  text: msg.messageUser,
                  isAdmin: false,
                  timestamp: msg.requestTime,
                });
              }
              
              // Add admin/bot message
              if (msg.messageBot) {
                sessionMap[sessionId].messages.push({
                  text: msg.messageBot,
                  isAdmin: true,
                  timestamp: msg.responseTime,
                  adminName: msg.adminName || "ADMIN",
                });
              }
            });
            
            // Convert to array and set timestamps
            self.chatSessions = Object.values(sessionMap).map((session) => {
              if (session.messages.length > 0) {
                session.startTime = session.messages[0]?.timestamp;
                session.endTime = session.messages[session.messages.length - 1]?.timestamp;
              }
              return session;
            });
            
            console.log('Chat Sessions:', self.chatSessions);
            // Scroll to bottom after DOM updates
            self.$nextTick(() => {
              self.scrollToBottom();
            });
          } else {
            self.chatSessions = [];
          }
          self.isLoading = false;
        },
        (errorResponse) => {
          console.error('Error fetching conversation:', errorResponse);
          self.chatSessions = [];
          self.isLoading = false;
        }
      );
    },

    // Scroll to bottom of chat
    scrollToBottom() {
      const chatContent = this.$refs.chatContent?.$el || this.$refs.chatContent;
      if (chatContent) {
        chatContent.scrollTop = chatContent.scrollHeight;
      }
    },

    // Image Preview
    openImagePreview(url) {
      this.previewImageUrl = url;
      this.imagePreviewDialog = true;
    },
  },
  mounted() {
    this.GetDate();
    this.isInitialized = true;
    this.Get();
  },
};
</script>

<style scoped>
/* =============================================================================
   CARD COMPONENTS
   ============================================================================= */
.card2 {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.filter-card,
.action-card,
.advanced-filter-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.action-card {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.02), rgba(25, 118, 210, 0.05)) !important;
  border: 1px solid rgba(25, 118, 210, 0.2) !important;
}

.advanced-filter-card {
  background: rgba(248, 250, 252, 0.8) !important;
}

.data-table-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* =============================================================================
   FORM ELEMENTS
   ============================================================================= */
.date-field .v-field {
  border-radius: 8px !important;
}

.section-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  border-bottom: 2px solid rgba(25, 118, 210, 0.2);
  padding-bottom: 8px;
}

/* =============================================================================
   BUTTONS
   ============================================================================= */
.search-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  min-width: 120px !important;
}

.chat-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.chat-btn:hover {
  transform: scale(1.02);
}

/* =============================================================================
   FILTER PANEL
   ============================================================================= */
.filter-panel-title {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.1)) !important;
  border-radius: 8px 8px 0 0 !important;
}

/* =============================================================================
   DATA TABLE
   ============================================================================= */
.table-container {
  overflow-x: auto;
  max-width: 100%;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.1);
}

.table-container::-webkit-scrollbar {
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: rgba(25, 118, 210, 0.5);
}

.modern-table {
  border-radius: 0 0 12px 12px !important;
}

.table-row {
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

.text-container {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}

.text-content {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  line-height: 1.4;
  word-break: break-word;
}

/* =============================================================================
   CHAT DIALOG - MODERN DESIGN
   ============================================================================= */
.chat-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
  animation: dialogFadeIn 0.3s ease;
}

@keyframes dialogFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.chat-dialog-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%);
  color: white;
  padding: 0 !important;
  position: relative;
  overflow: hidden;
}

.chat-dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.chat-header-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px 20px;
  position: relative;
  z-index: 1;
}

.chat-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.chat-icon-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.chat-icon {
  color: white !important;
}

.chat-header-text {
  display: flex;
  flex-direction: column;
}

.chat-title {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.chat-subtitle {
  font-size: 0.85rem;
  opacity: 0.8;
  margin-top: 2px;
  font-family: monospace;
}

.header-stats {
  display: flex;
  align-items: center;
}

.header-chip {
  backdrop-filter: blur(4px);
  border-color: rgba(255, 255, 255, 0.4) !important;
  font-weight: 500;
}

.close-btn {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: rotate(90deg);
}

/* =============================================================================
   CHAT MESSAGES - ENHANCED
   ============================================================================= */
.chat-dialog-content {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  max-height: 65vh;
  overflow-y: auto;
  padding: 0 !important;
}

.chat-messages-wrapper {
  padding: 24px;
}

.chat-messages-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.chat-session {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* =============================================================================
   SESSION DIVIDERS - SIMPLE (Original Size)
   ============================================================================= */
.session-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
}

.session-divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(76, 175, 80, 0.4), transparent);
}

.session-line-end-color {
  background: linear-gradient(90deg, transparent, rgba(244, 67, 54, 0.4), transparent);
}

.session-chip {
  flex-shrink: 0;
  font-weight: 600;
  font-size: 0.75rem;
}

.session-time-info {
  text-align: center;
  margin-bottom: 8px;
}

.session-date {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.05);
  padding: 4px 12px;
  border-radius: 12px;
}

/* =============================================================================
   MESSAGE LAYOUT - PREMIUM ENHANCED
   ============================================================================= */
.message-wrapper {
  display: flex;
  flex-direction: column;
}

.message-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  max-width: 75%;
}

.user-row {
  align-self: flex-start;
}

.admin-row {
  align-self: flex-end;
}

/* Message Avatar - Premium Style */
.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.message-avatar::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.message-avatar:hover::before {
  left: 100%;
}

.user-avatar {
  background: linear-gradient(135deg, #78909c 0%, #546e7a 50%, #455a64 100%);
}

.admin-avatar {
  background: linear-gradient(135deg, #42a5f5 0%, #1e88e5 50%, #1565c0 100%);
}

/* Message Bubble - Premium Style */
.message-bubble {
  padding: 14px 18px;
  border-radius: 20px;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  animation: messageSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.message-bubble:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(15px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.user-bubble {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  color: #1e293b;
  border-bottom-left-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.user-bubble::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: -8px;
  width: 0;
  height: 0;
  border: 8px solid transparent;
  border-right-color: #ffffff;
  border-bottom-color: #ffffff;
  border-bottom-left-radius: 4px;
}

.admin-bubble {
  background: linear-gradient(135deg, #42a5f5 0%, #1e88e5 100%);
  color: white;
  border-bottom-right-radius: 6px;
  position: relative;
  overflow: hidden;
}

.admin-bubble::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
  pointer-events: none;
}

.admin-bubble::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: -8px;
  width: 0;
  height: 0;
  border: 8px solid transparent;
  border-left-color: #1e88e5;
  border-bottom-color: #1e88e5;
  border-bottom-right-radius: 4px;
}

.message-sender {
  font-size: 0.7rem;
  font-weight: 700;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.user-bubble .message-sender {
  color: #64748b;
}

.admin-bubble .message-sender {
  color: rgba(255, 255, 255, 0.85);
}

.message-content {
  font-size: 0.95rem;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  position: relative;
  z-index: 1;
}

.message-time {
  font-size: 0.68rem;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-bubble .message-time {
  color: #94a3b8;
  justify-content: flex-start;
  text-align: left;
}

.admin-bubble .message-time {
  justify-content: flex-end;
  text-align: right;
}

.admin-bubble .message-time {
  color: rgba(255, 255, 255, 0.75);
}

/* Attachments - Premium Style */
.message-attachment {
  margin-top: 12px;
  position: relative;
  z-index: 1;
}

.attachment-image {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.attachment-image:hover {
  transform: scale(1.03) translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.attachment-file {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 12px;
  margin-top: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.user-bubble .attachment-file {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border: 1px solid #e2e8f0;
}

.user-bubble .attachment-file:hover {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  transform: translateX(4px);
}

.admin-bubble .attachment-file {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
}

.admin-bubble .attachment-file:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(4px);
}

.file-name {
  font-size: 0.85rem;
  font-weight: 600;
}

/* Empty State - Premium */
.chat-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.02) 100%);
}

.empty-icon-wrapper {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
}

/* Image Preview - Premium */
.image-preview-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.3) !important;
}

/* =============================================================================
   SCROLLBAR STYLING - PREMIUM
   ============================================================================= */
.chat-dialog-content::-webkit-scrollbar {
  width: 8px;
}

.chat-dialog-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.chat-dialog-content::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
}

.chat-dialog-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #2563eb, #1e40af);
}

/* =============================================================================
   RESPONSIVE - ENHANCED
   ============================================================================= */
@media (max-width: 768px) {
  .message-row {
    max-width: 88%;
  }

  .chat-title {
    font-size: 1rem;
  }

  .chat-subtitle {
    font-size: 0.75rem;
  }

  .filter-row {
    gap: 8px;
  }

  .header-stats {
    display: none;
  }

  .chat-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .session-badge-start,
  .session-badge-end {
    padding: 6px 12px;
  }

  .session-badge-content {
    display: none;
  }

  .session-badge-icon,
  .session-badge-icon-end {
    margin: 0;
  }
}

@media (max-width: 600px) {
  .stats-bar {
    justify-content: center;
  }

  .chat-messages-wrapper {
    padding: 16px;
  }

  .message-bubble {
    padding: 10px 12px;
  }
}

/* =============================================================================
   FILTER SECTION
   ============================================================================= */
.filter-row {
  margin: 0 -8px;
}

.filter-row > * {
  padding: 4px 8px;
}

.stats-bar {
  display: flex;
  align-items: center;
}

.stats-chip {
  font-weight: 500;
}

/* =============================================================================
   ANIMATIONS
   ============================================================================= */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>
